import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { 
  Package, 
  Megaphone, 
  Settings, 
  Image, 
  LogOut,
  LayoutDashboard,
  Menu,
  X
} from "lucide-react";

const AdminLayout = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    navigate("/admin/login");
  };

  const navItems = [
    { path: "/admin/urunler", icon: Package, label: "Ürünler" },
    { path: "/admin/duyurular", icon: Megaphone, label: "Duyurular" },
    { path: "/admin/parametreler", icon: Settings, label: "Parametreler" },
    { path: "/admin/resimler", icon: Image, label: "Resimler" },
  ];

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="flex h-screen bg-gray-50" data-testid="admin-layout">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-slate-900 text-white rounded-lg shadow-lg"
        data-testid="mobile-menu-button"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-slate-900 text-white p-4 lg:p-6 flex flex-col gap-4
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="admin-sidebar"
      >
        {/* Close button for mobile */}
        <button
          onClick={closeSidebar}
          className="lg:hidden absolute top-4 right-4 p-1 text-slate-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Logo */}
        <div className="flex items-center gap-3 mb-6 lg:mb-8 pb-4 lg:pb-6 border-b border-slate-700">
          <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center">
            <span className="text-white text-lg lg:text-xl font-bold font-serif">C</span>
          </div>
          <div>
            <h1 className="text-lg lg:text-xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              CK-DER
            </h1>
            <p className="text-slate-400 text-[10px] lg:text-xs">Admin Panel</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 lg:space-y-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg transition-all duration-200 text-sm lg:text-base ${
                  isActive ? "bg-amber-500/20 text-amber-400" : "text-slate-300 hover:bg-slate-800"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-4 h-4 lg:w-5 lg:h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer Links */}
        <div className="pt-4 lg:pt-6 border-t border-slate-700 space-y-1 lg:space-y-2">
          <a 
            href="/" 
            className="flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 text-sm lg:text-base"
            data-testid="nav-home"
          >
            <LayoutDashboard className="w-4 h-4 lg:w-5 lg:h-5" />
            <span>Ana Sayfa</span>
          </a>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg w-full text-red-400 hover:text-red-300 hover:bg-red-500/10 text-sm lg:text-base"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 lg:w-5 lg:h-5" />
            <span>Çıkış Yap</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto p-4 lg:p-6 pt-16 lg:pt-6" data-testid="admin-content">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
